import { DateTime } from 'luxon'
import { FormikProvider, useFormik } from 'formik'

import {
  Accordion,
  Alert,
  Button,
  Card,
  CardColors,
  Form,
  Popup,
  Sheet,
  Text,
} from '@/ui'
import {
  upsertUserIncomeMonth,
  UserIncomePlanMonth,
} from '@/features/income-planning/incomePlanning.slice'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { TypographyStyles } from '@/styles'
import { getFieldNames, makeNumberSchema } from '@/base'
import {
  centsToDollars,
  dollarsToCents,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  NonTherapyIncomePopup,
  PerSessionRatePopup,
  SessionsPerMonthPopup,
  TherapyIncomePopup,
  TotalPlannedIncomePopup,
} from '@/features/income-planning/IncomePlanningPopups'

export const IncomeMonthSheet = ({
  monthToShow,
  closeSheet,
}: {
  monthToShow: UserIncomePlanMonth | null
  closeSheet: () => void
}) => {
  const dispatch = useAppDispatch()
  const formik = useFormik({
    initialValues: {
      plannedIncomeAmount: centsToDollars(
        monthToShow?.totalPlannedIncomeInCents
      ),
      notes: monthToShow?.notes,
    },
    enableReinitialize: true,
    onSubmit: async ({ plannedIncomeAmount, notes }) => {
      if (!monthToShow) {
        return
      }

      await dispatch(
        upsertUserIncomeMonth({
          totalPlannedIncomeInCents: dollarsToCents(plannedIncomeAmount),
          notes,
          month: monthToShow.month,
          year: monthToShow.year,
        })
      )

      closeSheet()
    },
  })

  const fieldNames = getFieldNames(formik)
  const { touched, submitForm } = formik

  if (!monthToShow) {
    return null
  }

  const wasPlanApplied = Boolean(monthToShow.nonTherapyIncomeInCents)

  return (
    <Sheet
      open={Boolean(monthToShow)}
      onOpenChange={(val) => !val && closeSheet()}
    >
      <FormikProvider value={formik}>
        <Sheet.Content>
          <Sheet.Title>
            {DateTime.fromObject({
              month: monthToShow.month,
              year: monthToShow.year,
            }).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)}
          </Sheet.Title>
          <Text variant={TypographyStyles.BODY_SM} className="mt-6">
            {wasPlanApplied
              ? 'You applied a plan to this month. Change the planned income amount below, or add notes for yourself.'
              : 'You customized your planned income by entering the amount manually. Change the amount below, or add notes for yourself.'}
          </Text>
          <div className="mt-6">
            <Form.Input
              name={fieldNames.plannedIncomeAmount}
              label="Planned Income Amount"
              schema={makeNumberSchema({ allowedDecimals: 2, minNumber: 0 })}
              componentType="currency"
            />
          </div>
          {touched.plannedIncomeAmount && wasPlanApplied && (
            <Alert
              variant="warning"
              title="Entering an amount manually will overwrite any existing information, including amounts that were copied from any plans applied to this month."
              className="mt-2"
            />
          )}
          {wasPlanApplied && (
            <Card color={CardColors.LEVEL_1} className="mt-6">
              <Text variant={TypographyStyles.DISPLAY_SM}>
                {formatCurrencyFromCents(monthToShow.totalPlannedIncomeInCents)}
              </Text>
              <Popup
                content={<TotalPlannedIncomePopup />}
                trigger={
                  <Text variant={TypographyStyles.POPUP_LINK}>
                    Total Planned Income
                  </Text>
                }
              />
              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-4">
                {monthToShow.therapyIncomeInCents
                  ? formatCurrencyFromCents(monthToShow.therapyIncomeInCents)
                  : '$-'}
              </Text>
              <Popup
                content={<TherapyIncomePopup />}
                trigger={
                  <Text variant={TypographyStyles.POPUP_LINK}>
                    Therapy Income
                  </Text>
                }
              />

              {Boolean(monthToShow.nonTherapyIncomeInCents) && (
                <>
                  <Text variant={TypographyStyles.DISPLAY_SM} className="mt-4">
                    {formatCurrencyFromCents(
                      monthToShow.nonTherapyIncomeInCents
                    )}
                  </Text>
                  <Popup
                    content={<NonTherapyIncomePopup />}
                    trigger={
                      <Text variant={TypographyStyles.POPUP_LINK}>
                        Non-Therapy Income
                      </Text>
                    }
                  />
                </>
              )}

              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-4">
                {monthToShow.ratePerSession
                  ? formatCurrencyFromCents(monthToShow.ratePerSession)
                  : '$-'}
              </Text>
              <Popup
                content={<PerSessionRatePopup />}
                trigger={
                  <Text variant={TypographyStyles.POPUP_LINK}>
                    Blended per Session Rate
                  </Text>
                }
              />
              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-4">
                {monthToShow.sessionsPerMonth || '-'}
              </Text>
              <Popup
                content={<SessionsPerMonthPopup />}
                trigger={
                  <Text variant={TypographyStyles.POPUP_LINK}>
                    Sessions per Month
                  </Text>
                }
              />
            </Card>
          )}
          <Accordion variant="text" type="single" collapsible className="mt-6">
            <Accordion.Item value="notes-accordion">
              <Accordion.Trigger>
                <Text>Notes</Text>
              </Accordion.Trigger>
              <Accordion.Content>
                <Form.Textarea
                  name={fieldNames.notes}
                  placeholder="Use this space to add additional context about this plan"
                />
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
          <Sheet.Footer>
            <div className="justify-between w-full flex">
              <Button variant="secondary" onClick={closeSheet}>
                Cancel
              </Button>
              <Button onClick={submitForm}>Save</Button>
            </div>
          </Sheet.Footer>
        </Sheet.Content>
      </FormikProvider>
    </Sheet>
  )
}
